import Constants from '@/Constants';
import { useMetadata } from '@/hooks/useMetadata';
import { ContentMetadata } from '@/models/ContentMetadata';
import Item from '@/models/Item';
import { LanguageType } from '@/models/LanguageType';
import { contentService } from '@/services/ContentService';
import { ApplicationState } from '@/store';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import './style.scss';

interface Props {
    content: Item;
    metadata: ContentMetadata;
    startLesson(): void;
    finishLesson(): void;
    lessonStatus: string;
}

const ExternalPlayer: React.FC<Props> = (props: Props) => {
    const { content, metadata, lessonStatus, startLesson, finishLesson } = props;

    const { strings, currentContentLanguage } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        currentContentLanguage: state.locale.currentContentLanguage || (state.locale.data._LANGUAGE as LanguageType),
    }));

    const setCurrentPath = useCallback((): string => {
        if (content) {
            if (currentContentLanguage && currentContentLanguage.length > 0) {
                return contentService.getContentPathByLanguageType(content, currentContentLanguage);
            }
        }
        return '';
    }, [content, currentContentLanguage]);

    const [currentContentPath, setCurrentContentPath] = useState<string>(setCurrentPath);

    const { metadata: linkMetadata } = useMetadata(currentContentPath);

    const getIcon = (type: string): string => {
        switch (type) {
            case strings.AUDIO_TYPE_NAME:
                return Constants.icons.MICROPHONE;
            case strings.VIDEO_TYPE_NAME:
                return Constants.icons.VIDEOCAM;
            case strings.EXTERNAL_LINK:
                return Constants.icons.LINK;
            default:
                return Constants.icons.FILE_DOCUMENT_OUTLINE;
        }
    };

    const loadNewPathByNewLanguage = useCallback(() => {
        if (content && currentContentLanguage) {
            setCurrentContentPath(contentService.getContentPathByLanguageType(content, currentContentLanguage));
        }
    }, [currentContentLanguage]);

    useEffect(() => {
        if (lessonStatus === strings.NOT_ATTEMPTED) {
            startLesson();
        }
    }, []);

    useEffect(loadNewPathByNewLanguage, [currentContentLanguage]);

    const onPressVideoHandler = (): void => {
        finishLesson();
    };

    return (
        <a className="lms-external-player-link" href={content.path} rel="noopener noreferrer" target="_blank">
            <div className="box-link">
                <div className="box-link__cover" onClick={onPressVideoHandler}>
                    {linkMetadata && linkMetadata.image && <img alt="" className="box-link__cover-thumb" src={linkMetadata.image} />}
                    <div className="box-link__cover-overlay"></div>
                    <div className="box-link__cover-icon">
                        <span className="material-icons-outlined">{getIcon(metadata.typeName)}</span>
                    </div>
                </div>
            </div>
        </a>
    );
};

export default ExternalPlayer;
